import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  output,
  signal,
  inject,
  WritableSignal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import * as _ from 'lodash';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ISelectOptionGroups } from 'src/app/models/selectOptions/selectOptionGroups.interface';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { InLineControlFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inLineControlFilters.model';
import { DynamicMultiSelectFilters } from 'src/app/models/filters/implementations/filtersTypes/dynamicFilter.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccordionComponent } from 'src/app/components/accordion/accordion.component';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import {
  SearchActivityHttpService,
  AutocompleteResponse,
} from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, tap, BehaviorSubject } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AutocompleteApiComponent } from 'src/app/components/autocomplete-api/autocomplete-api.component';
import { UntypedFormControl } from '@angular/forms';

interface IDynamicFilterConfig {
  readonly control: FormControl<string | null>;
  readonly filterType: FilterTypesEnum;
  readonly placeholder: string;
  readonly title: string;
  readonly selectedOptions: WritableSignal<ISelectOption[]>;
  readonly getFilter: () => DynamicMultiSelectFilters;
}

interface IInLineControlDynamicFilters {
  readonly environment: IDynamicFilterConfig;
  readonly threadId: IDynamicFilterConfig;
}

const DEFAULT_DEBOUNCE = 1000;
const MIN_CHARS_FOR_FILTER = 3;

@Component({
  selector: 'app-in-line-control-vertical-filters',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    AccordionComponent,
    BeautifyDisplayStringPipe,
    MatProgressSpinnerModule,
    AutocompleteApiComponent,
  ],
  templateUrl: './in-line-control-vertical-filters.component.html',
  styleUrls: ['./in-line-control-vertical-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InLineControlVerticalFiltersComponent implements OnInit {
  public readonly pageFilters = input.required<InLineControlFiltersModel>();
  public readonly isFiltersMenuOpen = input.required<boolean>();

  public readonly filtersOnMenuStateChange = output<boolean>();
  public readonly optionOnSelection = output<ISelectOption[]>();

  public readonly optionsInsideCategories = signal<ISelectOptionGroups[]>([]);
  public readonly expandedCategories = signal<{ [optionGroup: string]: boolean }>({});
  public readonly selectedIds = signal<Set<string>>(new Set());

  public readonly dynamicFilters: IInLineControlDynamicFilters = {
    environment: {
      control: new FormControl(''),
      filterType: FilterTypesEnum.InLineEnvironment,
      placeholder: 'Enter Environment ID...',
      title: 'Environment Id',
      selectedOptions: signal<ISelectOption[]>([]),
      getFilter: () => this.pageFilters().environmentFilters,
    },
    threadId: {
      control: new FormControl(''),
      filterType: FilterTypesEnum.InLineThreadId,
      placeholder: 'Enter Thread ID...',
      title: 'Thread Id',
      selectedOptions: signal<ISelectOption[]>([]),
      getFilter: () => this.pageFilters().threadIdFilters,
    },
  };

  public readonly searchActorValuesCtrl = new UntypedFormControl();
  public filteredActorValues: ISelectOption[] = [];
  public readonly isActorLoading$ = new BehaviorSubject<boolean>(false);
  public readonly actorFilterEnum = FilterTypesEnum.InLineActor;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _searchActivityHttpService = inject(SearchActivityHttpService);

  ngOnInit(): void {
    this.pageFilters()
      .allOptions$.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options: ISelectOption[]) => {
        this._updateOptionsInsideCategories(options);
      });

    this.pageFilters()
      .onUpdate$.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._updateSelectedIdsFromFilterModel();
        this._updateSelectedOptionsSignals();
      });

    this._updateSelectedIdsFromFilterModel();
    this._updateSelectedOptionsSignals();
    this._setDefaultExpandedCategories();
    this._initActorFilter();
  }

  public onToggleClick() {
    this.filtersOnMenuStateChange.emit(!this.isFiltersMenuOpen());
  }

  public expandClicked(optionGroupName: string, status: boolean) {
    this.expandedCategories.update((categories) => ({
      ...categories,
      [optionGroupName]: status,
    }));
  }

  public onSelect(id: string): void {
    this.selectedIds.update((currentIds) => {
      const newIds = new Set(currentIds);
      if (newIds.has(id)) {
        newIds.delete(id);
      } else {
        newIds.add(id);
      }
      return newIds;
    });
    this.optionOnSelection.emit(this._getSelectedOptions());
  }

  public toggleDynamicFilterSelection(
    filterKey: keyof IInLineControlDynamicFilters,
    selectedOption: ISelectOption
  ): void {
    const filter = this.dynamicFilters[filterKey].getFilter();
    const currentlySelected = filter.getSelectedOptions();
    const isCurrentlySelected = currentlySelected.some((option) => option.id === selectedOption.id);

    // Update local state to reflect the change immediately
    if (isCurrentlySelected) {
      this.selectedIds.update((ids) => {
        const newIds = new Set(ids);
        newIds.delete(selectedOption.id);
        return newIds;
      });
    } else {
      this.selectedIds.update((ids) => {
        const newIds = new Set(ids);
        newIds.add(selectedOption.id);
        return newIds;
      });
    }

    // Emit the selection change to parent component - parent will handle filter update and query params
    const selectedOptions = this._getSelectedOptions();
    this.optionOnSelection.emit(selectedOptions);
  }

  public onThreadIdSearch(): void {
    this.onDynamicFilterSearch('threadId');
  }

  public onThreadIdKeyPress(event: KeyboardEvent): void {
    this.onDynamicFilterKeyPress('threadId', event);
  }

  public onEnvironmentIdSearch(): void {
    this.onDynamicFilterSearch('environment');
  }

  public onEnvironmentIdKeyPress(event: KeyboardEvent): void {
    this.onDynamicFilterKeyPress('environment', event);
  }

  public onDynamicFilterSearch(filterKey: keyof IInLineControlDynamicFilters): void {
    const filterConfig = this.dynamicFilters[filterKey];
    const inputValue = filterConfig.control.value?.trim();
    if (!inputValue) return;

    const newOption: ISelectOption = {
      id: inputValue,
      value: inputValue,
      displayName: inputValue,
      filterType: filterConfig.filterType,
      categoryName: filterConfig.filterType,
      withoutBeautifyDisplayName: true,
    };

    const filter = filterConfig.getFilter();
    const currentlySelected = filter.getSelectedOptions();
    const isAlreadySelected = currentlySelected.some((option) => option.value === inputValue);

    if (!isAlreadySelected) {
      const currentOptions = filter.getOptions$().value;
      const existingOption = currentOptions.find((option) => option.value === inputValue);

      if (!existingOption) {
        const allOptions = [...currentOptions, newOption];
        filter.updateOptions(allOptions);
      }

      // Update local state to show the new selection immediately
      this.selectedIds.update((ids) => {
        const newIds = new Set(ids);
        newIds.add(inputValue);
        return newIds;
      });

      // Emit the selection change to parent component - parent will handle filter update and query params
      const selectedOptions = this._getSelectedOptions();
      this.optionOnSelection.emit(selectedOptions);
    }

    filterConfig.control.setValue('');
  }

  public onDynamicFilterKeyPress(filterKey: keyof IInLineControlDynamicFilters, event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onDynamicFilterSearch(filterKey);
    }
  }

  public toggleEnvironmentSelection(selectedOption: ISelectOption): void {
    this.toggleDynamicFilterSelection('environment', selectedOption);
  }

  public toggleThreadIdSelection(selectedOption: ISelectOption): void {
    this.toggleDynamicFilterSelection('threadId', selectedOption);
  }

  public actorSubmitted(actors: ISelectOption[] = []): void {
    this.pageFilters().actorFilters.updateOptions(_.union(this.pageFilters().actorFilters.getOptions$().value, actors));
    this.pageFilters().updateFilterFromUserSelection(undefined, _.union(this._getSelectedOptions(), actors));
  }

  public getSelectedActorOptions(): ISelectOption[] {
    return this.pageFilters().actorFilters.getSelectedOptions();
  }

  public toggleActorSelection(selectedOption: ISelectOption): void {
    const filter = this.pageFilters().actorFilters;
    const currentlySelected = filter.getSelectedOptions();
    const isCurrentlySelected = currentlySelected.some((option) => option.id === selectedOption.id);

    if (isCurrentlySelected) {
      this.selectedIds.update((ids) => {
        const newIds = new Set(ids);
        newIds.delete(selectedOption.id);
        return newIds;
      });
    } else {
      this.selectedIds.update((ids) => {
        const newIds = new Set(ids);
        newIds.add(selectedOption.id);
        return newIds;
      });
    }

    const selectedOptions = this._getSelectedOptions();
    this.optionOnSelection.emit(selectedOptions);
  }

  public getSelectedEnvironmentOptions(): ISelectOption[] {
    return this.dynamicFilters.environment.selectedOptions();
  }

  public getSelectedThreadIdOptions(): ISelectOption[] {
    return this.dynamicFilters.threadId.selectedOptions();
  }

  private _getSelectedOptions(): ISelectOption[] {
    const selectedIndexArray = Array.from(this.selectedIds());

    // Build optionsById from all individual filter options to ensure we have the latest data
    const allOptions = [
      ...this.pageFilters().severityFilters.getOptions$().value,
      ...this.pageFilters().actorFilters.getOptions$().value,
      ...this.pageFilters().integrationFilters.getOptions$().value,
      ...this.pageFilters().aiServiceFilters.getOptions$().value,
      ...this.pageFilters().environmentFilters.getOptions$().value,
      ...this.pageFilters().threadIdFilters.getOptions$().value,
    ];
    const optionsById = Object.fromEntries(allOptions.map((option) => [option.id, option]));

    // This is needed because there are options that can be selected but are not shown.
    const selectedOptions = selectedIndexArray.map((id: string) => optionsById[id]);

    return selectedOptions.filter((option) => option !== undefined);
  }

  private _updateSelectedIdsFromFilterModel() {
    // Update selectedIds based on what's actually selected in the filter model
    const allSelectedOptions = this.pageFilters().getAllSelectedOptions();
    const newSelectedIds = new Set<string>();
    allSelectedOptions.forEach((option) => {
      newSelectedIds.add(option.id);
    });
    this.selectedIds.set(newSelectedIds);
  }

  private _updateSelectedOptionsSignals() {
    // Update the selected options signals to ensure UI reactivity
    this.dynamicFilters.environment.selectedOptions.set(this.pageFilters().environmentFilters.getSelectedOptions());
    this.dynamicFilters.threadId.selectedOptions.set(this.pageFilters().threadIdFilters.getSelectedOptions());
  }

  private _updateOptionsInsideCategories(options: ISelectOption[]) {
    const groupedOptions = _.groupBy(options, 'categoryName');
    const optionGroups: ISelectOptionGroups[] = [];

    // Define the order of static filters
    const staticFilterOrder = [
      FilterTypesEnum.InLineSeverity,
      FilterTypesEnum.InLineIntegration,
      FilterTypesEnum.InLineAiService,
    ];

    staticFilterOrder.forEach((filterType) => {
      const categoryOptions = groupedOptions[filterType];
      if (categoryOptions && categoryOptions.length > 0) {
        optionGroups.push({
          displayName: filterType,
          options: categoryOptions,
          isDynamic: false,
          selectedCount: categoryOptions.filter((option) => this.selectedIds().has(option.id)).length,
        });
      }
    });

    this.optionsInsideCategories.set(optionGroups);
  }

  private _setDefaultExpandedCategories() {
    this.expandedCategories.set({
      [FilterTypesEnum.InLineSeverity]: false,
      [FilterTypesEnum.InLineIntegration]: false,
      [FilterTypesEnum.InLineAiService]: false,
    });
  }

  private _initActorFilter() {
    this.searchActorValuesCtrl.valueChanges
      .pipe(
        filter((res) => res !== null && res?.length >= MIN_CHARS_FOR_FILTER),
        distinctUntilChanged(),
        debounceTime(DEFAULT_DEBOUNCE),
        tap(() => {
          this.filteredActorValues.length = 0;
          this.isActorLoading$.next(true);
        }),
        switchMap((value) =>
          this._searchActivityHttpService.getAutocompleteOptions('findings', 'actor', value ?? '').pipe(
            finalize(() => {
              this.isActorLoading$.next(false);
            })
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((response: AutocompleteResponse) => {
        for (const suggestion of response.suggestions) {
          this.filteredActorValues.push({
            id: `${FilterTypesEnum.InLineActor}-${suggestion.value}`,
            value: suggestion.value,
            displayName: suggestion.value,
            filterType: FilterTypesEnum.InLineActor,
            categoryName: FilterTypesEnum.InLineActor,
            withoutBeautifyDisplayName: true,
          } as ISelectOption);
        }
      });
  }
}
